import { z } from 'zod';

// WHY: the checksum coefficients below are fixed by the FNS algorithm
// referenced from Приказ ФНС N ММВ-7-6/435@. The order states that the
// check digits are "calculated by a special algorithm" without publishing
// the coefficients; the values here match the de-facto implementation used
// across 1C, СБИС, Контур, and Госуслуги ЕСКС, and are mirrored verbatim
// from the reference Go validator in `miss/.code/internal/clients/inn.go`.
const OOO_WEIGHTS = [2, 4, 10, 3, 5, 9, 4, 6, 8] as const;
const IP_W11 = [7, 2, 4, 10, 3, 5, 9, 4, 6, 8] as const;
const IP_W12 = [3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8] as const;

function digitsOf(inn: string): number[] | null {
  if (!/^\d+$/.test(inn)) return null;
  const out = new Array<number>(inn.length);
  for (let i = 0; i < inn.length; i++) {
    out[i] = inn.charCodeAt(i) - 48;
  }
  return out;
}

// WHY: FNS spec uses mod 11; when the residue is 10, the check digit is 0,
// hence the second mod 10. Same convention applies to both INN forms and to
// the historical Go reference algorithm.
function weightedMod11(digits: readonly number[], weights: readonly number[]): number {
  let sum = 0;
  for (let i = 0; i < weights.length; i++) {
    sum += digits[i]! * weights[i]!;
  }
  return (sum % 11) % 10;
}

/**
 * @aiPurpose checksum-verify a 10-digit OOO INN per the FNS algorithm.
 * @aiInvariant returns `true` iff the input is exactly 10 ASCII digits AND
 *   `digits[9]` equals the weighted-mod-11 check digit of `digits[0..8]`.
 */
export function isValidInnOoo(inn: string): boolean {
  const d = digitsOf(inn);
  if (!d || d.length !== 10) return false;
  return weightedMod11(d, OOO_WEIGHTS) === d[9];
}

/**
 * @aiPurpose checksum-verify a 12-digit IP INN per the FNS algorithm.
 * @aiInvariant returns `true` iff the input is exactly 12 ASCII digits AND
 *   both `digits[10]` and `digits[11]` match their independent weighted-
 *   mod-11 check digits.
 */
export function isValidInnIp(inn: string): boolean {
  const d = digitsOf(inn);
  if (!d || d.length !== 12) return false;
  return weightedMod11(d, IP_W11) === d[10] && weightedMod11(d, IP_W12) === d[11];
}

/**
 * @aiPurpose accept either a valid 10-digit OOO INN or a valid 12-digit IP
 *   INN. Length disambiguates the form; the wire layer uses this validator
 *   for cross-form acceptance (e.g. `innSchema`), and type-bound validation
 *   (an OOO must be 10 digits, an IP must be 12) is enforced at the legal-
 *   entity schema level via `.superRefine` once `type` is known.
 */
export function isValidInn(inn: string): boolean {
  return isValidInnOoo(inn) || isValidInnIp(inn);
}

/**
 * @aiPurpose strict Russian INN validator. Accepts 10-digit OOO and 12-digit
 *   IP forms with checksum-verified last digit(s) per the FNS algorithm
 *   (Приказ ММВ-7-6/435@). Type-bound check (OOO must be 10 digits, IP must
 *   be 12) lives at the legal-entity create schema, not here.
 * @aiInvariant accepted values round-trip byte-equal: `innSchema.parse(v) === v`.
 *   No normalisation (trim, strip dashes, etc.) — wire callers must send the
 *   canonical digit-only form.
 */
export const innSchema = z.string().refine(isValidInn, { message: 'invalid INN' });
